'use client';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import type { OverviewSection } from '@hooks/search/useSearchOverview';
import SearchResultCard, { SearchResultType } from './_SearchResultCard';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';

interface Props {
  searchType: SearchResultType;
  label: string;
  data: OverviewSection;
  isFetching: boolean;
  onViewAll: (type: string) => void;
}

export const CARDS_SHOWN = 5;

const GRID_CLASSES = 'grid grid-cols-1 xs:grid-cols-2 md:grid-cols-5 gap-4';

const SearchSectionRow = ({ searchType, label, data, isFetching, onViewAll }: Props) => {
  const { t } = useTranslation();

  if (!isFetching && data.nbHits === 0) return null;

  return (
    <section className="mb-10" aria-label={label}>
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="mb-0 tra-text-web-heading-5 inline-flex items-end">
          {label}
          {!isFetching && data.nbHits > 0 && (
            <span className="ml-2 text-neutral-500 tra-text-web-body-l font-normal">
              ({data.nbHits.toLocaleString()})
            </span>
          )}
        </h2>
        {!isFetching && data.nbHits > CARDS_SHOWN && (
          <Button variant={ButtonVariant.knife} onClick={() => onViewAll(searchType)}>
            {t('gridContent.viewAll', undefined, { default: 'View All' })}
          </Button>
        )}
      </div>

      {isFetching && data.results.length === 0 ? (
        <div
          className={GRID_CLASSES}
          aria-label={t('search.loading', undefined, { default: 'Loading results' })}
        >
          {Array.from({ length: CARDS_SHOWN }).map((_, i) => (
            <div
              key={i}
              className="h-[280px] rounded-lg overflow-hidden relative bg-warm-200"
              aria-hidden="true"
            >
              <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/40 to-transparent" />
            </div>
          ))}
        </div>
      ) : (
        <div className={GRID_CLASSES}>
          {data.results.slice(0, CARDS_SHOWN).map((result: any, i: number) => (
            <div key={`${searchType}_${i}`} className="relative">
              <SearchResultCard result={result} searchType={searchType} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default SearchSectionRow;
